/* eslint-disable class-methods-use-this */
import { User as FirestoreUser } from "firebase/auth";
import {
  addDoc,
  collection,
  collectionGroup,
  deleteDoc,
  doc,
  DocumentData,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  QuerySnapshot,
  runTransaction,
  setDoc,
  Unsubscribe,
  updateDoc,
  where,
} from "firebase/firestore";
import {
  getBlob,
  getStorage,
  ref,
  uploadBytes,
  UploadResult,
} from "firebase/storage";
import { db as idb } from "src/api/db";
import { resolvePartnerForIdentity } from "src/features/users/services/fetchPartner";
import { AttendanceFormData } from "src/pages/DashboardPage/RangeTab/RangeAttendanceForm";
import { ReferenceFormData } from "src/pages/OnboardingScreen/references/ReferenceForm";
import { ProofOfIncomeType } from "src/shared/compliance/incomeDocuments";
import isAdminGuard from "src/shared/utils/validation/isAdminGuard";
import isAttendanceGuard from "src/shared/utils/validation/isAttendanceGuard";
import isCohortGuard from "src/shared/utils/validation/isCohortGuard";
import isNoteGuard from "src/shared/utils/validation/isNoteGuard";
import isOrganizationGuard from "src/shared/utils/validation/isOrganizationGuard";
import isQuizGradeGuard from "src/shared/utils/validation/isQuizGradeGuard";
import isStudentReference from "src/shared/utils/validation/isStudentReferenceGuard";
import { Organization, OrganizationId } from "src/types";
import { Admin } from "src/types/Admin";
import { Attendance } from "src/types/Attendance";
import { Cohort } from "src/types/Cohort";
import { Note } from "src/types/Note";
import { Partner } from "src/types/Partner";
import { StudentReference } from "src/types/StudentReference";
import { UserVideoProgress } from "src/types/StudyPlan";
import { UserAccount, UserOnboardingStatus, UserType } from "src/types/User";
import { getFullName, timeout } from "src/utils";
import sortBy from "src/utils/sortBy";
import { LIB_VERSION } from "src/version";
import { db } from ".";
import cleanFirestoreDoc from "./cleanFirestoreDoc";
import generateFirestoreTimestamps from "./generateFirestoreTimestamps";

export type GradeReportItem = {
  questionId: string;
  questionText: string;
  selectedAnswerId: string;
  selectedAnswerText: string;
  correctAnswerId: string;
  correctAnswerText: string;
  explanation?: string;
  isCorrect: boolean;
  topicId?: string; // Legacy: used by QuizPage (DatoCMS)
  atomId?: string; // New: used by QuizScreenV2 (Firestore)
  cognitiveLevel?: "recall" | "comprehension" | "application" | "analysis";
};

export type QuizGrade = {
  uid: string;
  /** Optional - legacy records have it, new records use auto-generated doc ID */
  quizId?: string;
  grade: number;
  totalQuestions: number;
  totalCorrectAnswers: number;
  quizType?: string;
  quizCategory?: string;
  // was added to the schema after the first version -- adding it to create
  lastUpdatedAt?: string;
  gradeReport?: GradeReportItem[];
  /**
   * Finer label than `quizType` for analysis. "section_exam" marks an exam
   * served inside the course at a section boundary (see
   * src/features/section-exams/). Absent on every other grade.
   */
  quizSubtype?: string;
  /** Which section exam produced this grade (e.g. "gk_checkpoint_1"). */
  sectionExamId?: string;
};

// Caller payload for setUserResourceProgress. `kind: "video"` is stamped by the
// writer; callers do not pass it.
export type UpdateResourcePayload = Pick<
  UserVideoProgress,
  "progressSeconds" | "resourceId" | "progressFraction" | "hasCompleted"
>;

export type UpdateBookPayload = {
  location: string | number;
};

export type CurrentUser = FirestoreUser & UserAccount;

// Mirror of shared-utils/enrollmentDocuments.ts — CRA's ModuleScopePlugin
// blocks src→shared-utils imports, so the frontend keeps a local literal.
// Change the list in BOTH places.
export const requiredEnrollmentDocuments = [
  "ssn",
  "driver_license",
  "birth_certificate",
  "proof_of_income",
  "utility_bill",
] as const;

/**
 * Legacy income document keys kept for backward compatibility.
 * Mirror of shared-utils/enrollmentDocuments.ts (see note above).
 */
export const LEGACY_INCOME_DOC_KEYS = ["pay_stub", "food_stamps"] as const;
export type LegacyIncomeDocument = typeof LEGACY_INCOME_DOC_KEYS[number];
export type RequiredEnrollmentDocument =
  typeof requiredEnrollmentDocuments[number];

const optionalEnrollmentDocuments: string[] = [];

export type OptionalEnrollmentDocument =
  typeof optionalEnrollmentDocuments[number];

const milestoneDocuments = [
  "updated_resume",
  "license",
  "theory_permit",
  "medical_exam",
  "completion_certificate",
  "job_offer",
  "cover_letter",
  "background_check",
  "driver_record",
  "job_retention_30d",
  "job_retention_180d",
] as const;

export type MilestoneDocument = typeof milestoneDocuments[number];

const schedulingProofDocuments = [
  "theory_permit_scheduling",
  "medical_exam_scheduling",
] as const;

export type SchedulingProofDocument = typeof schedulingProofDocuments[number];

export type StudentDocument =
  | RequiredEnrollmentDocument
  | OptionalEnrollmentDocument
  | MilestoneDocument
  | SchedulingProofDocument
  | LegacyIncomeDocument;

const LOCAL_STORAGE_USER_KEY = "LOCAL_STORAGE_USER_KEY";

export function getSerializedUser(): string | null {
  return localStorage.getItem(LOCAL_STORAGE_USER_KEY);
}

export type SessionAccountInformation =
  | (UserAccount & { type: UserType.User })
  | (Admin & { type: UserType.Admin })
  | (Partner & { type: UserType.Partner });

// On a fresh registration the Firebase Auth user is created (Step 1 of
// AuthContext.registerStudent) before its Firestore profile document is written
// (Step 2). The onAuthStateChanged listener fires on Step 1 and immediately
// initializes this client, so the profile read can lose the race against that
// write — or against Firestore eventual consistency. Retry the account lookup a
// few times before treating the account as missing.
const ACCOUNT_LOOKUP_RETRIES = 4;
const ACCOUNT_LOOKUP_RETRY_DELAY_MS = 500;
const RETRYABLE_ACCOUNT_LOOKUP_ERROR_CODES = new Set([
  "permission-denied",
  "unavailable",
]);

const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

const isRetryableAccountLookupError = (error: unknown): boolean => {
  if (typeof error !== "object" || error === null || !("code" in error)) {
    return false;
  }

  return RETRYABLE_ACCOUNT_LOOKUP_ERROR_CODES.has(
    String(error.code).replace(/^firestore\//, "")
  );
};

export default class FirestoreClient {
  // TODO: update it to add admin type as well
  metadata = null as SessionAccountInformation | null;

  credentials = {} as FirestoreUser;

  loading = true;

  constructor(private readonly user: FirestoreUser) {
    this.credentials = user;
  }

  // Resolves the account document backing the current auth credentials.
  // Returns true and populates `metadata` on success, false if no matching
  // user/partner/admin document exists yet.
  private async resolveAccount(): Promise<boolean> {
    const user = await this.fetchUserAccount();

    if (user) {
      this.metadata = { ...user, type: UserType.User };
      await this.updateCurrentUserMetadata("users");
      return true;
    }

    const partner = await this.fetchPartner();
    if (partner) {
      // An alias shares the partner record but is not the primary account.
      // Do not overwrite the primary partner's last-login metadata with an
      // additional dashboard user's activity.
      if (partner.uid === this.user.uid) {
        await this.updateCurrentUserMetadata("partners");
      }
      this.metadata = { ...partner, type: UserType.Partner };
      return true;
    }

    const admin = await this.fetchAdmin();

    if (admin) {
      await this.updateCurrentUserMetadata("admins");

      this.metadata = { ...admin, type: UserType.Admin };
      return true;
    }

    return false;
  }

  private async initialize(
    attemptsRemaining = ACCOUNT_LOOKUP_RETRIES
  ): Promise<void> {
    try {
      if (await this.resolveAccount()) return;
    } catch (error) {
      // Immediately after Firebase Auth emits a newly-created user, Firestore
      // can briefly evaluate the profile read before it has received that
      // user's auth token. Treat that the same as the document-write race and
      // retry instead of surfacing the emulator/rules error in a browser alert.
      if (attemptsRemaining > 0 && isRetryableAccountLookupError(error)) {
        await delay(ACCOUNT_LOOKUP_RETRY_DELAY_MS);
        await this.initialize(attemptsRemaining - 1);
        return;
      }

      throw error;
    }

    // The account document may simply not have been written yet on a fresh
    // registration (see ACCOUNT_LOOKUP_RETRIES note above). Back off and retry
    // before giving up, so a brand-new account isn't wrongly reported missing.
    if (attemptsRemaining > 0) {
      await delay(ACCOUNT_LOOKUP_RETRY_DELAY_MS);
      await this.initialize(attemptsRemaining - 1);
      return;
    }

    // Retries exhausted: the auth credentials exist but no account document was
    // found (e.g. the rollback in registerStudent failed to delete the orphaned
    // auth user).
    throw new Error(
      "Email credentials exist but the user account has not been set up yet"
    );
  }

  static async create(user: FirestoreUser) {
    const o = new FirestoreClient(user);
    await o.initialize();
    return o;
  }

  // demographic survey
  async fetchUserAccount(): Promise<UserAccount | undefined> {
    const snapshot = await getDoc(this.getUserDoc());

    // TODO: stop manually casting
    const data = cleanFirestoreDoc(snapshot);

    this.loading = false;

    return data as UserAccount | undefined;
  }

  async updateUserAccount(metadata: UserAccount) {
    this.metadata = { ...metadata, type: UserType.User };
  }

  subscribeToUserResourceProgress(
    cb: (snapshot: QuerySnapshot<DocumentData>) => void,
    id?: string
  ): Unsubscribe {
    const unsubscribe = onSnapshot(
      collection(db, "users", id || this.credentials.uid, "resources"),
      cb
    );
    return unsubscribe;
  }

  async setUserResourceProgress(
    resourceId: string,
    payload: UpdateResourcePayload
  ): Promise<void> {
    const ref = doc(db, `users/${this.user.uid}/resources/`, resourceId);
    const now = new Date();
    // Stamp `createdAt` once, on the first progress write, and never again — it
    // is the stable "resource started" timestamp the permit analytics use as a
    // completion proxy (`lastUpdatedAt` drifts when a finished video is
    // rewatched). Transaction + merge mirror `upsertStudyGuideProgress` for this
    // same subcollection; merge also preserves `createdAt`/`hasCompleted` across
    // later writes instead of the previous full-overwrite dropping them.
    await runTransaction(db, async (tx) => {
      const existing = await tx.get(ref);
      const createdTimestamps = existing.exists()
        ? {}
        : generateFirestoreTimestamps(now, "createdAt");
      tx.set(
        ref,
        {
          userId: this.user.uid,
          ...generateFirestoreTimestamps(now, "lastUpdatedAt"),
          ...payload,
          ...createdTimestamps,
          // `kind` last so an `as any` payload can't override the discriminator.
          kind: "video",
        },
        { merge: true }
      );
    });
  }

  async setUserGrade(
    quizRes: Omit<QuizGrade, "uid"> & { quizId: string }
  ): Promise<void> {
    // Note: firebase has a clever implementation that does not resolve until the user's network connection is restored
    // once we have an API this functionality won't be built-in
    // I am manually forcing a timeout here (similar to what will happen when we have an actual API)
    try {
      await timeout(
        3000,
        setDoc(doc(db, `users/${this.user.uid}/grades/`, quizRes.quizId), {
          userId: this.user.uid,
          ...generateFirestoreTimestamps(new Date(), "lastUpdatedAt"),
          ...quizRes,
        })
      );
    } catch (err) {
      // fallback is to cache as an offline actions for later
      await idb.upsertOfflineAction({
        id: quizRes.quizId,
        type: "quiz",
        payload: quizRes,
      });
    }
  }

  async setBookPage(bookId: string, payload: UpdateBookPayload): Promise<void> {
    try {
      await timeout(
        3000,
        setDoc(doc(db, `users/${this.user.uid}/books/`, bookId), {
          ...payload,
        })
      );
    } catch (err) {
      // fallback is to cache as an offline actions for later
      await idb.upsertOfflineAction({ id: bookId, type: "book", payload });
    }
  }

  getUserDoc() {
    return doc(db, "users", this.user.uid);
  }

  // TODO: once we finalize migrating all the accounts to roles we need to revisit this
  async updateCurrentUserStatus(onboardingStatus: UserOnboardingStatus) {
    updateDoc(doc(db, `users/${this.user.uid}`), {
      onboardingStatus,
    });
  }

  async updateCurrentUserMetadata(collection: "users" | "admins" | "partners") {
    await updateDoc(doc(db, `${collection}/${this.user.uid}`), {
      ...generateFirestoreTimestamps(new Date(), "lastLoggedIn"),
      appVersion: LIB_VERSION,
    });
  }

  async updateCurrentUser(userId: string, update: Partial<UserAccount>) {
    updateDoc(doc(db, `users/${userId}`), {
      ...update,
      ...generateFirestoreTimestamps(new Date(), "lastUpdatedAt"),
    });
  }

  async uploadStudentDocument(
    document: {
      file: File;
      type: StudentDocument;
      proofType?: ProofOfIncomeType;
    },
    id?: string
  ): Promise<UploadResult> {
    const storage = getStorage();
    const userId = id || this.user.uid;
    const storageRef = ref(
      storage,
      `documents/${userId}/${document.type}-${document.file.name}`
    );
    const res = await uploadBytes(storageRef, document.file);
    await setDoc(doc(db, `users/${userId}/documents/`, document.type), {
      path: res.ref.fullPath,
      userId,
      name: document.type,
      ...(document.proofType && { proofType: document.proofType }),
    });

    return res;
  }

  async createAttendance(
    { photo, date, ...rest }: AttendanceFormData,
    id?: string
  ): Promise<Attendance> {
    const storage = getStorage();
    const userId = id || this.user.uid;

    const storageRef = ref(
      storage,
      `attendance/${userId}/${new Date().toISOString()}`
    );

    const payload: Omit<Attendance, "uid"> = {
      userId,
      ...rest,
      ...generateFirestoreTimestamps(new Date(), "createdAt"),
      ...generateFirestoreTimestamps(date, "date"),
    };

    if (photo) {
      const res = await uploadBytes(storageRef, photo);
      payload.path = res.ref.fullPath;
    }
    const doc = await addDoc(
      collection(db, "users", userId, "attendance"),
      payload
    );

    const attendance = cleanFirestoreDoc(await getDoc(doc));

    if (!isAttendanceGuard(attendance))
      throw new Error("Something went wrong creating the attendance record");

    return attendance;
  }

  async deleteAttendance(
    clientId: string,
    attendanceId: string
  ): Promise<void> {
    await deleteDoc(doc(db, `users/${clientId}/attendance/${attendanceId}`));
  }

  async fetchAttendanceRecords(userId?: string): Promise<Attendance[]> {
    const id = userId || this.user.uid;

    const snapshot = await getDocs(
      query(collection(db, "users", id, "attendance"))
    );

    return snapshot.docs.map(cleanFirestoreDoc).filter(isAttendanceGuard);
  }

  async getStorageBlob(
    type: StudentDocument | string,
    userId?: string
  ): Promise<File | undefined> {
    const storage = getStorage();
    const snapshot = await getDoc(
      doc(db, "users", userId || this.user.uid, "documents", type)
    );
    const data = snapshot.data();

    if (!data) return undefined;

    const file = new File([await getBlob(ref(storage, data.path))], data.path);

    return file;
  }

  // Student reference

  async createStudentReference(
    data: ReferenceFormData,
    userId?: string
  ): Promise<StudentReference> {
    const id = userId || this.user.uid;

    const res = await addDoc(collection(db, "users", id, "references"), {
      ...data,
      userId: id,
      ...generateFirestoreTimestamps(new Date(), "createdAt"),
    });

    const doc = cleanFirestoreDoc(await getDoc(res));

    if (!isStudentReference(doc)) {
      throw new Error("Erro creating reference");
    }

    return doc;
  }

  async fetchStudentReferences(userId?: string) {
    const id = userId || this.user.uid;
    const snapshot = await getDocs(collection(db, "users", id, "references"));
    return snapshot.docs.map(cleanFirestoreDoc).filter(isStudentReference);
  }

  async fetchOrganization(
    organizationId: OrganizationId
  ): Promise<Organization | undefined> {
    const snapshot = await getDoc(doc(db, "organizations", organizationId));
    const data = cleanFirestoreDoc(snapshot);
    if (!isOrganizationGuard(data))
      throw new Error("Organization does not exist");
    return data;
  }

  async fetchOrganizationClients(
    organizationId: OrganizationId
  ): Promise<UserAccount[]> {
    // get all clients affiliated with the organization
    const clientsSnapshot = await getDocs(
      query(
        collection(db, "users"),
        where("organizationId", "==", organizationId)
      )
    );
    const clients = await Promise.all(
      clientsSnapshot.docs.map(async (doc) => {
        const client = cleanFirestoreDoc(doc) as UserAccount;
        return client;
      })
    );

    return clients;
  }

  async fetchClientGrades(
    userId: string
  ): Promise<(QuizGrade & { label: string })[]> {
    const snapshot = await getDocs(collection(db, "users", userId, "grades"));

    const grades = snapshot.docs
      .map(cleanFirestoreDoc)
      .filter(isQuizGradeGuard);

    function createGradeLabel(grade: number) {
      if (grade <= 0.1) return "0-10";
      if (grade <= 0.2) return "10-20";
      if (grade <= 0.3) return "20-30";
      if (grade <= 0.4) return "30-40";
      if (grade <= 0.5) return "40-50";
      if (grade <= 0.6) return "50-60";
      if (grade <= 0.7) return "60-70";
      if (grade <= 0.8) return "70-80";
      if (grade <= 0.9) return "80-90";
      return "90-100";
    }

    return grades.map((grade) => ({
      ...grade,
      label: createGradeLabel(grade.grade),
    }));
  }

  async fetchClient(uid: string): Promise<UserAccount> {
    if (!this.metadata) throw new Error("Needs to be logged in");

    const snapshot = await getDoc(doc(db, "users", uid));
    const client = cleanFirestoreDoc(snapshot) as UserAccount;

    return client;
  }

  async updateNote(
    clientId: string,
    noteId: string,
    update: Pick<Note, "description" | "date">
  ): Promise<void> {
    await updateDoc(doc(db, `users/${clientId}/notes/${noteId}`), update);
  }

  async deleteNote(clientId: string, noteId: string): Promise<void> {
    await deleteDoc(doc(db, `users/${clientId}/notes/${noteId}`));
  }

  async createNote(
    data: Omit<
      Note,
      | "uid"
      | "dateFirestoreTimestamp"
      | "createdAt"
      | "createdAtFirestoreTimestamp"
      | "lastUpdatedAt"
      | "lastUpdatedAtFirestoreTimestamp"
      | "author"
      | "authorId"
      | "authorOrganization"
    >,
    userId: string
  ): Promise<Note> {
    if (!this.metadata) throw new Error("Cannot load information");
    if (this.metadata.type === UserType.User)
      throw new Error("Cannot create case notes");

    const authorship: Pick<Note, "author" | "authorId" | "authorOrganization"> =
      {
        author: getFullName(this.metadata),
        authorOrganization: "Emerge Career",
        authorId: this.metadata.uid,
      };

    const doc = await addDoc(collection(db, "users", userId, "notes"), {
      ...data,
      ...generateFirestoreTimestamps(new Date(data.date), "date"),
      ...generateFirestoreTimestamps(new Date(), "createdAt"),
      ...generateFirestoreTimestamps(new Date(), "lastUpdatedAt"),
      ...authorship,
    });

    const note = cleanFirestoreDoc(await getDoc(doc));
    if (!isNoteGuard(note)) throw new Error("Something went wrong");
    return note;
  }

  // admin routes
  async fetchAdmin(): Promise<Admin | undefined> {
    const snapshot = await getDoc(doc(db, "admins", this.user.uid));

    const data = cleanFirestoreDoc(snapshot);

    if (!isAdminGuard(data)) return undefined;

    return data;
  }

  // next step - figure out authentication. maybe hash password?
  async fetchPartner(): Promise<Partner | undefined> {
    return await resolvePartnerForIdentity(this.user, { db });
  }

  async fetchUsers(): Promise<UserAccount[]> {
    const snapshot = await getDocs(
      query(collection(db, "users"), where("onboardingStatus", "!=", ""))
    );

    const users = snapshot.docs.map(cleanFirestoreDoc) as UserAccount[];

    return users;
  }

  async fetchOrganizations(): Promise<Organization[]> {
    const organizations = (
      await getDocs(collection(db, "organizations"))
    ).docs.map((snap) => cleanFirestoreDoc(snap)) as Organization[];

    return organizations;
  }

  async updateUserOrganization(
    user: Pick<UserAccount, "uid">,
    organizationId: OrganizationId
  ) {
    updateDoc(doc(db, `users/${user.uid}`), {
      organizationId,
    });
  }

  async updateUserEmergeCoach(
    user: Pick<UserAccount, "uid">,
    emergeCoachId: string
  ) {
    await updateDoc(doc(db, `users/${user.uid}`), {
      emergeCoachId,
    });
  }

  async updateUserSecondaryEmergeCoach(
    user: Pick<UserAccount, "uid">,
    secondaryEmergeCoachId: string
  ) {
    await updateDoc(doc(db, `users/${user.uid}`), {
      secondaryEmergeCoachId,
    });
  }

  async fetchCohorts(): Promise<Cohort[]> {
    const cohorts = (await getDocs(collectionGroup(db, "cohorts"))).docs
      .map(cleanFirestoreDoc)
      .filter(isCohortGuard);

    return sortBy(cohorts, "name");
  }

  async fetchOrganizationCohorts(
    organizationId: OrganizationId
  ): Promise<Cohort[]> {
    const cohorts = (
      await getDocs(collection(db, "organizations", organizationId, "cohorts"))
    ).docs
      .map(cleanFirestoreDoc)
      .filter(isCohortGuard);

    return sortBy(cohorts, "name");
  }

  async updateCohort(
    client: Pick<UserAccount, "uid">,
    cohortId: string
  ): Promise<void> {
    await updateDoc(doc(db, `users/${client.uid}`), {
      cohortId,
    });
  }

  // Student Employment Plan
}
